"use client";

import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AstrologyDisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export function AstrologyDisclaimerModal({ isOpen, onClose, onAccept }: AstrologyDisclaimerModalProps) {
  const [isChecked, setIsChecked] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (isChecked) {
      onAccept();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-slate-200">Astroloji Hakkında</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Astroloji, gökyüzünde gezegenlerin ve yıldızların hareketlerini inceleyerek insan hayatına dair çıkarımlar yapmayı amaçlayan kadim bir disiplindir. <strong className="text-amber-300">Bilimsel olarak kesinliği kanıtlanmış bir alan değildir</strong>; yani astroloji, tıp ya da fizik gibi akademik bir bilim dalı olarak kabul edilmez. Ancak bu, astrolojinin değerini ya da insanlar üzerindeki etkisini küçültmez.
            </p>

            <p>
              Binlerce yıldır farklı kültürlerde gelişen astroloji, insan deneyimlerini semboller ve gökyüzü hareketleri üzerinden yorumlamayı öğrenmiştir. Bu yorumlar, istatistiksel gözlemler ve tarih boyunca aktarılan deneyimlerin harmanıyla oluşur. Bu yüzden astroloji, hayatınızı şekillendirecek bir kesinlik sunmaktan ziyade, size bir bakış açısı kazandırır.
            </p>

            <p>
              Astrolojiye, yolculuğunuzda size eşlik eden bir <strong className="text-blue-300">pusula gibi bakabilirsiniz</strong>. Kararlarınızı sizin yerinize vermez ama kendi potansiyelinizi, güçlü ve zayıf yanlarınızı daha iyi tanımanıza yardımcı olabilir. Onu, hayatınızı kolaylaştıracak ve kendinizi anlamanızı destekleyecek bir rehber olarak değerlendirmek, astrolojiden en sağlıklı şekilde faydalanmanın anahtarıdır.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              id="astrology-accept"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label
              htmlFor="astrology-accept"
              className="text-sm text-slate-300 cursor-pointer"
            >
              Yukarıdaki açıklamaları okudum ve astrolojinin doğası hakkında bilgilendirildiğimi kabul ediyorum.
            </label>
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Kapat
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!isChecked}
              className={`${
                isChecked
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-slate-700 text-slate-500 cursor-not-allowed"
              } transition-colors`}
            >
              Devam Et
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}